import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Modal, Form, Checkbox, Radio } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import {
  Arrow,
  FormCard,
  FormItem,
  Heading,
  Input,
  TextArea
} from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import StockCount from '../iconsV2/StockCount'
import { Button, Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import Asterix from 'screens/Rostering/RotaPattern/components/Asterix'
import StockBanner from './StockBanner'
import AdditionalDetailsSection from './AdditionalDetailsSection'
import { useStockCheck } from '../hooks/useStockCheck'

type StockCheckModalProps = {
  open: boolean
  medicationName: string
  expectedStock: number
  residentId: string
  residentMedicationId: string
  onCancel: () => void
  onSuccess?: () => void
  editMode?: boolean
  initialData?: Partial<StockCheckData> & { recordId?: string }
}

export type StockCheckData = {
  description: string
  stockCount: number
  adjustment: number
  additionalDetails: boolean
  isExpectedStockAccurate: boolean
  resolveDiscrepancy?: string | null
  discrepancyReason?: string
  media?: File[]
}

const StockCheckModal: React.FC<StockCheckModalProps> = ({
  open,
  medicationName,
  expectedStock,
  residentId,
  residentMedicationId,
  onCancel,
  onSuccess,
  editMode = false,
  initialData
}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const [form] = Form.useForm()

  // Compute initial values from initialData
  const initialValues =
    editMode && initialData
      ? {
          description: initialData.description || '',
          resolveDiscrepancy: initialData.resolveDiscrepancy || null,
          discrepancyReason: initialData.discrepancyReason || ''
        }
      : {
          description: '',
          resolveDiscrepancy: null,
          discrepancyReason: ''
        }

  const [stockCount, setStockCount] = useState<number | null>(
    editMode && initialData?.stockCount !== undefined
      ? initialData.stockCount
      : null
  )
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(
    (editMode && initialData?.additionalDetails) || false
  )
  const [fileList, setFileList] = useState<any[]>([])
  const [isExpectedStockAccurate, setIsExpectedStockAccurate] = useState(
    editMode && initialData?.stockCount !== undefined
      ? initialData.stockCount === expectedStock
      : false
  )
  const [showAdjustmentMode, setShowAdjustmentMode] = useState(false)
  const [adjustmentInput, setAdjustmentInput] = useState<number | null>(null)
  const [adjustmentError, setAdjustmentError] = useState<string | null>(null)
  const [resolveDiscrepancy, setResolveDiscrepancy] = useState<string | null>(
    (editMode && initialData?.resolveDiscrepancy) || null
  )
  const [discrepancyReason, setDiscrepancyReason] = useState<string>(
    (editMode && initialData?.discrepancyReason) || ''
  )

  // Reset state when modal closes (minimal useEffect only for cleanup)
  useEffect(() => {
    if (!open) {
      setStockCount(null)
      setAdjustmentInput(null)
      setAdjustmentError(null)
      setShowAdditionalDetails(false)
      setIsExpectedStockAccurate(false)
      setShowAdjustmentMode(false)
      setResolveDiscrepancy(null)
      setDiscrepancyReason('')
      setFileList([])
      form.resetFields()
    }
  }, [open, form])

  const handleFileChange = (info: any) => {
    setFileList(info.fileList)
  }

  const resetInputs = () => {
    setStockCount(null)
    setAdjustmentInput(null)
    setAdjustmentError(null)
  }

  const { createStockCheck, isLoading } = useStockCheck({
    residentId,
    residentMedicationId,
    expectedStock,
    onSuccess: () => {
      form.resetFields()
      resetInputs()
      setShowAdditionalDetails(false)
      setIsExpectedStockAccurate(false)
      setShowAdjustmentMode(false)
      onSuccess?.()
    }
  })

  const handleEnterAdjustment = () => {
    resetInputs()
    setShowAdjustmentMode(true)
  }

  const handleSetStockCount = () => {
    resetInputs()
    setShowAdjustmentMode(false)
  }

  const handleStockCountChange = (value: string) => {
    if (!value || value === '') {
      setStockCount(null)
      return
    }
    const numVal = Number(value)
    if (!isNaN(numVal)) {
      setStockCount(numVal)
    }
  }

  const handleAdjustmentInputChange = (value: string) => {
    if (!value || value === '') {
      resetInputs()
      return
    }

    const numVal = Number(value)
    if (isNaN(numVal)) return

    setAdjustmentInput(numVal)

    if (numVal < -expectedStock) {
      setAdjustmentError(t('ADJUSTMENT_TOO_HIGH'))
      setStockCount(null)
    } else {
      setAdjustmentError(null)
      setStockCount(expectedStock + numVal)
    }
  }

  const handleSave = () => {
    form.validateFields().then((values) => {
      const finalStockCount = stockCount ?? 0
      const adjustment = finalStockCount - expectedStock
      const data: StockCheckData = {
        description: values.description || '',
        stockCount: finalStockCount,
        adjustment,
        additionalDetails: showAdditionalDetails,
        isExpectedStockAccurate,
        resolveDiscrepancy: resolveDiscrepancy,
        discrepancyReason: values.discrepancyReason || discrepancyReason || undefined
      }
      createStockCheck(data)
    })
  }

  const currentStockCount = isExpectedStockAccurate ? expectedStock : stockCount
  const discrepancy =
    currentStockCount !== null ? currentStockCount - expectedStock : null
  const hasDiscrepancy = discrepancy !== null && discrepancy !== 0
  const hasError = adjustmentError !== null

  const getDiscrepancyDisplay = () => {
    if (isExpectedStockAccurate) return expectedStock
    if (showAdjustmentMode) {
      return hasError || adjustmentInput === null ? '' : stockCount
    }
    return stockCount === null
      ? ''
      : `${discrepancy! > 0 ? '+' : ''}${discrepancy!}`
  }

  // Use key to force remount when switching between edit/new or different records
  const modalKey =
    editMode && initialData?.recordId ? `edit-${initialData.recordId}` : 'new'

  return (
    <Modal
      key={modalKey}
      open={open}
      onCancel={onCancel}
      footer={null}
      width={648}
      title={
        <TitleContainer>
          <StockCount />
          <ModalTitle>
            <Heading level={3}>
              {editMode ? t('EDIT_STOCK_CHECK') : t('STOCK_CHECK')}
            </Heading>
          </ModalTitle>
        </TitleContainer>
      }
    >
      <Divider />

      <ModalContentContainer>
        <Form form={form} initialValues={initialValues}>
          <FormCard level={2}>
            <FormItem
              name="description"
              label={<Text>{t('DESCRIPTION')}</Text>}
              full
              rules={[
                { required: true, message: t('DESCRIPTION_REQUIRED') },
                { max: 150, message: t('DESCRIPTION_MAX_LENGTH') }
              ]}
            >
              <StyledTextArea
                rows={4}
                placeholder={t('ENTER_DESCRIPTION')}
                maxLength={150}
                showCount
                data-testid="stock-check-description"
              />
            </FormItem>
          </FormCard>
        </Form>

        <FormCard
          level={2}
          title={
            <CardTitleContainer>
              <MedicationNameHeading weight="bold" level={5}>
                {medicationName}
              </MedicationNameHeading>
              <Button
                variant="text"
                onClick={
                  showAdjustmentMode
                    ? handleSetStockCount
                    : handleEnterAdjustment
                }
              >
                {showAdjustmentMode
                  ? t('SET_STOCK_COUNT')
                  : t('ENTER_STOCK_ADJUSTMENT')}
              </Button>
            </CardTitleContainer>
          }
        >
          <AdjustmentContainer>
            <CheckboxContainer>
              <Checkbox
                checked={isExpectedStockAccurate}
                onChange={(e) => setIsExpectedStockAccurate(e.target.checked)}
              >
                <Text>{t('EXPECTED_STOCK_ACCURATE')}</Text>
              </Checkbox>
            </CheckboxContainer>
            <CalculationContainer>
              <CalculationColumn $align="center" $width="140px">
                <Label>{t('EXPECTED_STOCK')}</Label>
                <Value weight="bold">
                  {expectedStock} {t('TABLETS')}
                </Value>
              </CalculationColumn>
              {showAdjustmentMode ? (
                <PlusIconWrapper>
                  <PlusOutlined />
                </PlusIconWrapper>
              ) : (
                <IconWrapper>
                  <Arrow direction="right" size={18} />
                </IconWrapper>
              )}
              <CalculationColumn $flex>
                <LabelContainer>
                  <Asterix />
                  <Label>
                    {showAdjustmentMode ? t('ADJUSTMENT') : t('STOCK_COUNT')}
                  </Label>
                </LabelContainer>
                <Input
                  type="number"
                  value={
                    showAdjustmentMode
                      ? (adjustmentInput ?? '')
                      : (stockCount ?? '')
                  }
                  onChange={(e) => {
                    showAdjustmentMode
                      ? handleAdjustmentInputChange(e.target.value)
                      : handleStockCountChange(e.target.value)
                  }}
                  disabled={isExpectedStockAccurate}
                  status={hasError ? 'error' : undefined}
                  style={{ width: '100%' }}
                  data-testid="stock-count-input"
                  suffix={t('TABLETS')}
                />
              </CalculationColumn>
              <IconWrapper>
                <Arrow direction="right" size={18} />
              </IconWrapper>
              <CalculationColumn $align="center" $width="140px">
                <Label>
                  {showAdjustmentMode ? t('NEW_STOCK') : t('DISCREPANCY')}
                </Label>
                <DiscrepancyValue
                  $isPositive={discrepancy !== null && discrepancy >= 0}
                  $noDiscrepancy={discrepancy !== null && discrepancy === 0}
                  weight="bold"
                >
                  {getDiscrepancyDisplay()}
                </DiscrepancyValue>
              </CalculationColumn>
            </CalculationContainer>
            {hasError && (
              <StockBanner type="error">{adjustmentError}</StockBanner>
            )}
            {!hasError && !showAdjustmentMode && hasDiscrepancy && (
              <>
                <StockBanner type="warning">
                  {t('DISCREPANCY_DETECTED')}
                </StockBanner>

                <FormItem
                  name="resolveDiscrepancy"
                  label={<Text>{t('RESOLVE_DISCREPANCY')}</Text>}
                  required
                  rules={[
                    {
                      required: true,
                      message: t('RESOLVE_DISCREPANCY_REQUIRED')
                    }
                  ]}
                >
                  <StyledRadioGroup
                    value={resolveDiscrepancy}
                    onChange={(e) => setResolveDiscrepancy(e.target.value)}
                  >
                    <Radio value="no">
                      {<RadioLabel>{t('NO_LEAVE_OPEN')}</RadioLabel>}
                    </Radio>
                    <Radio value="yes">
                      {<RadioLabel>{t('YES_MARKED_RESOLVED')}</RadioLabel>}
                    </Radio>
                  </StyledRadioGroup>
                </FormItem>

                {resolveDiscrepancy === 'yes' && (
                  <FormItem
                    name="discrepancyReason"
                    label={<Text>{t('REASON_FOR_DISCREPANCY')}</Text>}
                    required
                    full
                    rules={[
                      {
                        required: true,
                        message: t('REASON_FOR_DISCREPANCY_REQUIRED')
                      }
                    ]}
                  >
                    <TextArea
                      rows={4}
                      value={discrepancyReason}
                      onChange={(e) => setDiscrepancyReason(e.target.value)}
                      placeholder={t('ENTER_REASON')}
                      maxLength={150}
                      showCount
                    />
                  </FormItem>
                )}
              </>
            )}
          </AdjustmentContainer>
        </FormCard>

        <AdditionalDetailsSection
          showAdditionalDetails={showAdditionalDetails}
          onToggle={setShowAdditionalDetails}
          fileList={fileList}
          onFileChange={handleFileChange}
        />
      </ModalContentContainer>

      <Divider />

      <ButtonsContainer>
        <Button onClick={onCancel} data-testid="cancel-btn">
          {t('CANCEL')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={hasError || isLoading}
          isLoading={isLoading}
          data-testid="save-btn"
        >
          {t('SAVE')}
        </Button>
      </ButtonsContainer>
    </Modal>
  )
}

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }
`

const ModalTitle = styled.div`
  text-transform: none;
  font-weight: normal;
`

const ModalContentContainer = styled.div`
  margin: 24px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MedicationNameHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const RadioLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CardTitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const CalculationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CalculationColumn = styled.div<{
  $align?: 'left' | 'center'
  $width?: string
  $flex?: boolean
}>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 0 8px;
  gap: 8px;
  align-items: ${({ $align = 'left' }) => $align};
  ${({ $width }) => $width && `width: ${$width};`}
  ${({ $flex }) => $flex && `flex: 1;`}
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;
`

const PlusIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 8px;

  .anticon {
    font-size: 18px;
    color: ${({ theme }) => theme.color.neutral.color1};
  }
`

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const Label = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
  min-height: 24px;
`

const Value = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color1};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const DiscrepancyValue = styled(Text)<{
  $isPositive: boolean
  $noDiscrepancy?: boolean
}>`
  color: ${({ $noDiscrepancy, theme }) =>
    $noDiscrepancy ? theme.color.status.success : theme.color.status.warning};
  align-self: center;
  min-height: 32px;
  display: flex;
  align-items: center;
`

const StyledTextArea = styled(TextArea)`
  width: 100%;
  margin-bottom: 16px;
`

const CheckboxContainer = styled.div`
  width: 100%;
`

const AdjustmentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-wrapper {
    background-color: transparent !important;
    margin: 0;
    padding: 4px;
  }

  .ant-radio-checked .ant-radio-inner {
    background-color: ${({ theme }) => theme.color.neutral.color1};
  }
`

const ButtonsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

export default StockCheckModal
